import { http } from './api'

// Factory for a namespaced read-only content module backed by a public
// backend endpoint (json-server-compatible bare arrays). The content resources
// are structurally identical, so they share this. Each module:
//   - starts empty and exposes `loading` / `error` so views can show state,
//   - fetches once and caches (navigating between pages wonʼt refetch),
//   - exposes `all` and `byId` getters plus a `fetch` action.
export default function createResourceModule({ path }) {
  return {
    namespaced: true,

    state: () => ({
      items: [],
      loaded: false,
      loading: false,
      error: null
    }),

    getters: {
      all: state => state.items,
      byId: state => id => state.items.find(i => String(i.id) === String(id)) || null
    },

    mutations: {
      setItems(state, items) { state.items = items; state.loaded = true },
      setLoading(state, value) { state.loading = value },
      setError(state, error) { state.error = error },
      setViews(state, { id, views }) {
        const item = state.items.find(i => String(i.id) === String(id))
        if (item) item.views = views
      }
    },

    actions: {
      // Cached by default: only hits the network the first time. Pass `true`
      // to force a refresh.
      async fetch({ commit, state }, force = false) {
        if (state.loaded && !force) return
        commit('setLoading', true)
        commit('setError', null)
        try {
          const { data } = await http.get(path)
          if (Array.isArray(data)) commit('setItems', data)
        } catch (err) {
          // Leave items as-is (empty, or the last good fetch) and record the error.
          commit('setError', err.message || 'Network error')
        } finally {
          commit('setLoading', false)
        }
      },

      // Best-effort view counter: tell the backend an item was opened (read /
      // viewed) and reflect the new total in the cached item. Deduplicated per
      // browser tab so a refresh or re-open doesn't keep inflating the count;
      // failures are swallowed since a missed view must never break the page.
      async incrementView({ commit }, id) {
        if (id == null) return
        const key = `viewed:${path}:${id}`
        try { if (sessionStorage.getItem(key)) return } catch (e) { /* storage blocked */ }
        try {
          const { data } = await http.post(`${path}/${id}/view`)
          if (data && typeof data.views === 'number') commit('setViews', { id, views: data.views })
          try { sessionStorage.setItem(key, '1') } catch (e) { /* storage blocked */ }
        } catch (e) { /* view counting is non-critical */ }
      }
    }
  }
}
